import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import {
  Shield, Monitor, Smartphone, Clock, AlertTriangle,
  CheckCircle2, XCircle, Trash2, RefreshCw, Bell, BellOff,
  Lock, Fingerprint,
} from 'lucide-react';
import { PasskeySetupModal } from '../../components/security/PasskeySetupModal';
import { DeviceRegistrationDialog } from '../../components/security/DeviceRegistrationDialog';
import { saveAccountToLocal } from '../../auth/savedAccounts';
import { markPasskeyOnThisDevice } from '../../auth/passkeyDevice';
import { getPasskeyStatus } from '../../security/passkey/passkeyApi';
import {
  getUserSecurityConfig,
  listAuthorizedDevices,
  listSecurityHistory,
  revokeAuthorizedDevice,
  updateUserSecurityConfig,
} from '../../security/userSecurityApi';

import { IncidentesSegurancaPage } from '../admin/seguranca/IncidentesSegurancaPage';

type Aba = 'dispositivos' | 'historico' | 'config' | 'incidentes';

interface DispositivoAutorizado {
  id: string;
  nome: string | null;
  navegador: string | null;
  sistemaOp: string | null;
  ip: string | null;
  aprovado: boolean;
  ultimoAcesso: string | null;
  esteDispositivo?: boolean;
  fingerprint: string;
}

interface AcessoHistorico {
  id: string;
  acao: string;
  ip: string | null;
  userAgent: string | null;
  criadoEm: string;
  restricao: string | null;
}

interface ConfigSeguranca {
  trustLevel: number;
  temFcm: boolean;
  confirmacaoCelularNovoDispositivo: boolean;
  alertasAcessoSuspeito: boolean;
}

const TRUST_LABEL: Record<number, { label: string; cor: string; desc: string }> = {
  1: { label: 'T1 — Identificado', cor: '#94a3b8', desc: 'Login básico com email verificado' },
  2: { label: 'T2 — Biométrico', cor: '#3b82f6', desc: 'Digital ou FaceID no celular' },
  3: { label: 'T3 — Presencial', cor: '#8b5cf6', desc: 'GPS dentro do perímetro escolar' },
  4: { label: 'T4 — Canal Push', cor: '#f59e0b', desc: 'OTP confirmado via notificação push' },
  5: { label: 'T5 — Estação Registrada', cor: '#10b981', desc: 'Passkey cadastrada nesta máquina' },
};

const ACAO_STYLE: Record<string, { label: string; cor: string; icone: React.ElementType }> = {
  LOGIN_SUCESSO:   { label: 'Login',          cor: '#10b981', icone: CheckCircle2 },
  LOGIN_FALHA:     { label: 'Falha',          cor: '#ef4444', icone: XCircle },
  ANOMALIA:        { label: 'Suspeito',       cor: '#f59e0b', icone: AlertTriangle },
  DISPOSITIVO_REVOGADO: { label: 'Revogado', cor: '#8b5cf6', icone: Trash2 },
  DEFAULT:         { label: 'Evento',         cor: '#64748b', icone: Clock },
};

function acessoStyle(acao: string) {
  for (const [key, val] of Object.entries(ACAO_STYLE)) {
    if (acao.toUpperCase().includes(key)) return val;
  }
  return ACAO_STYLE.DEFAULT;
}

export function PainelSegurancaPage() {
  const { user } = useAuthStore();
  const token = localStorage.getItem('ac_token') ?? '';
  const [aba, setAba] = useState<Aba>('dispositivos');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const [dispositivos, setDispositivos] = useState<DispositivoAutorizado[]>([]);
  const [historico, setHistorico] = useState<AcessoHistorico[]>([]);
  const [config, setConfig] = useState<ConfigSeguranca | null>(null);
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [hasPasskey, setHasPasskey] = useState<boolean | null>(null);
  const [showDeviceRegistration, setShowDeviceRegistration] = useState(false);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 3500); };

  const carregarDispositivos = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAuthorizedDevices({ bearerToken: token });
      setDispositivos(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const carregarHistorico = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listSecurityHistory(30, { bearerToken: token });
      setHistorico(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const carregarConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getUserSecurityConfig({ bearerToken: token });
      setConfig(data);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (aba === 'dispositivos') carregarDispositivos();
    if (aba === 'historico') carregarHistorico();
    if (aba === 'config') {
      carregarConfig();
      // Verificar se já tem Passkey cadastrada
      getPasskeyStatus({ bearerToken: token })
        .then(d => { if (d) setHasPasskey(!!d.temPasskey); })
        .catch(() => setHasPasskey(false));
    }
  }, [aba]);

  const revogarDispositivo = async (id: string) => {
    if (!confirm('Revogar acesso deste dispositivo?')) return;
    await revokeAuthorizedDevice(id, { bearerToken: token });
    showToast('Dispositivo revogado.');
    carregarDispositivos();
  };

  const toggleConfig = async (campo: keyof ConfigSeguranca) => {
    if (!config) return;
    const novoValor = !config[campo];
    await updateUserSecurityConfig({ [campo]: novoValor }, { bearerToken: token });
    setConfig(c => c ? { ...c, [campo]: novoValor } : c);
  };

  const trust = config ? (TRUST_LABEL[config.trustLevel] ?? TRUST_LABEL[1]) : null;

  return (
    <div style={{ padding: '2rem', maxWidth: 880, margin: '0 auto' }}>
      {/* Passkey Modal */}
      {showPasskeyModal && (
        <PasskeySetupModal
          onClose={() => setShowPasskeyModal(false)}
          onSuccess={(tl) => {
            setHasPasskey(true);
            setShowPasskeyModal(false);
            setConfig(c => c ? { ...c, trustLevel: tl } : c);
            showToast('Passkey cadastrada com sucesso! Trust Level elevado para T5.');
            if (user) {
              markPasskeyOnThisDevice(user.email);
              saveAccountToLocal(user);
            }
            // Abrir diálogo de cadastro do dispositivo
            setShowDeviceRegistration(true);
          }}
        />
      )}

      {/* Device Registration Dialog — após passkey concluída */}
      {showDeviceRegistration && (
        <DeviceRegistrationDialog
          fingerprint={[navigator.userAgent, screen.width, screen.height, navigator.language].join('|')}
          navegador={/Chrome/.test(navigator.userAgent) ? 'Chrome' : /Firefox/.test(navigator.userAgent) ? 'Firefox' : /Safari/.test(navigator.userAgent) ? 'Safari' : 'Outro'}
          sistemaOp={/Windows/.test(navigator.userAgent) ? 'Windows' : /Mac/.test(navigator.userAgent) ? 'macOS' : /Linux/.test(navigator.userAgent) ? 'Linux' : 'Outro'}
          onComplete={() => {
            setShowDeviceRegistration(false);
            carregarDispositivos();
            showToast('Dispositivo cadastrado com sucesso!');
          }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, background: '#1e293b', color: '#f8fafc',
          padding: '12px 20px', borderRadius: 12, fontSize: '0.85rem', fontWeight: 600,
          boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 9999,
        }}>{toast}</div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: '2rem' }}>
        <div style={{ width: 44, height: 44, borderRadius: 14, background: 'linear-gradient(135deg, #3b82f6, #6c5ce7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Shield size={22} style={{ color: '#fff' }} />
        </div>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 800, margin: 0, color: 'var(--text-main)' }}>Segurança da Conta</h1>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: 0 }}>
            {user?.nome} — Dispositivos, histórico de acessos e configurações de privacidade
          </p>
        </div>
      </div>

      {/* Trust Level Badge */}
      {trust && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px',
          borderRadius: 12, border: `1px solid ${trust.cor}44`, background: `${trust.cor}11`,
          marginBottom: '1.5rem',
        }}>
          <Lock size={18} style={{ color: trust.cor }} />
          <div>
            <p style={{ margin: 0, fontWeight: 700, fontSize: '0.9rem', color: trust.cor }}>{trust.label}</p>
            <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>{trust.desc}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: '1.5rem', background: 'var(--bg-card)', padding: 4, borderRadius: 12, border: '1px solid var(--border-color)' }}>
        {([
          { id: 'dispositivos', label: 'Dispositivos', Icone: Monitor },
          { id: 'historico',    label: 'Histórico de Acessos', Icone: Clock },
          { id: 'incidentes',   label: 'Incidentes', Icone: AlertTriangle },
          { id: 'config',       label: 'Configurações', Icone: Shield },
        ] as { id: Aba; label: string; Icone: React.ElementType }[]).map(({ id, label, Icone }) => (
          <button key={id} onClick={() => setAba(id)} style={{
            flex: 1, padding: '8px 12px', borderRadius: 9,
            border: 'none', cursor: 'pointer',
            background: aba === id ? 'var(--accent, #3b82f6)' : 'transparent',
            color: aba === id ? '#fff' : 'var(--text-muted)',
            fontWeight: aba === id ? 700 : 400, fontSize: '0.82rem',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            transition: 'all .15s',
          }}>
            <Icone size={14} /> {label}
          </button>
        ))}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
          <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      )}

      {/* ─── ABA: DISPOSITIVOS ─── */}
      {!loading && aba === 'dispositivos' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {dispositivos.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>Nenhum dispositivo autorizado encontrado.</p>
          ) : dispositivos.map(d => {
            const isMobile = d.sistemaOp?.toLowerCase().includes('android') || d.sistemaOp?.toLowerCase().includes('ios');
            const Icone = isMobile ? Smartphone : Monitor;
            return (
              <div key={d.id} style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '1rem 1.2rem',
                background: 'var(--bg-card)', border: `1px solid ${d.esteDispositivo ? '#3b82f644' : 'var(--border-color)'}`,
                borderRadius: 12, borderLeft: d.esteDispositivo ? '3px solid #3b82f6' : undefined,
              }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: d.aprovado ? '#10b98122' : '#f59e0b22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icone size={18} style={{ color: d.aprovado ? '#10b981' : '#f59e0b' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: '0 0 2px', fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-main)' }}>
                    {d.nome ?? `${d.navegador ?? 'Dispositivo'} — ${d.sistemaOp ?? 'Desconhecido'}`}
                    {d.esteDispositivo && <span style={{ marginLeft: 8, fontSize: '0.7rem', fontWeight: 600, color: '#3b82f6', background: '#3b82f622', padding: '2px 8px', borderRadius: 20 }}>Este dispositivo</span>}
                  </p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    {d.ip ?? 'IP desconhecido'} · Último acesso: {d.ultimoAcesso ? new Date(d.ultimoAcesso).toLocaleString('pt-BR') : 'Nunca'}
                  </p>
                </div>
                {!d.esteDispositivo && (
                  <button onClick={() => revogarDispositivo(d.id)} title="Revogar acesso" style={{
                    padding: '7px 12px', borderRadius: 8, border: '1px solid #ef444444',
                    background: '#ef444411', color: '#ef4444', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', fontWeight: 600,
                  }}>
                    <Trash2 size={13} /> Revogar
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ─── ABA: HISTÓRICO ─── */}
      {!loading && aba === 'historico' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {historico.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>Nenhum acesso registrado.</p>
          ) : historico.map(h => {
            const est = acessoStyle(h.acao);
            const StatusIcone = est.icone;
            const suspeito = h.restricao || h.acao.includes('FALHA') || h.acao.includes('ANOMALIA') || h.acao.includes('BLOQUEADO');
            return (
              <div key={h.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                background: suspeito ? `${est.cor}08` : 'var(--bg-card)',
                border: `1px solid ${suspeito ? est.cor + '33' : 'var(--border-color)'}`,
                borderRadius: 10,
              }}>
                <StatusIcone size={16} style={{ color: est.cor, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '0.83rem', fontWeight: 600, color: 'var(--text-main)' }}>
                    {est.label}
                    {h.ip && <span style={{ marginLeft: 8, color: 'var(--text-muted)', fontWeight: 400 }}>— {h.ip}</span>}
                  </p>
                  {h.restricao && (
                    <p style={{ margin: '2px 0 0', fontSize: '0.72rem', color: '#f59e0b' }}>⚠ {h.restricao}</p>
                  )}
                </div>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', flexShrink: 0 }}>
                  {new Date(h.criadoEm).toLocaleString('pt-BR')}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── ABA: INCIDENTES ─── */}
      {aba === 'incidentes' && <IncidentesSegurancaPage />}

      {/* ─── ABA: CONFIGURAÇÕES ─── */}
      {!loading && aba === 'config' && config && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Confirmação por celular */}
          {(['confirmacaoCelularNovoDispositivo', 'alertasAcessoSuspeito'] as const).map(campo => {
            const labels: Record<string, { titulo: string; desc: string; Icone: React.ElementType }> = {
              confirmacaoCelularNovoDispositivo: { titulo: 'Confirmação por celular em novos dispositivos', desc: 'Sempre que entrar em um computador novo, o celular pedirá confirmação.', Icone: Smartphone },
              alertasAcessoSuspeito: { titulo: 'Alertas de acesso suspeito', desc: 'Receba notificações quando alguém tentar usar sua conta.', Icone: Bell },
            };
            const { titulo, desc, Icone } = labels[campo];
            const ativo = config[campo] as boolean;
            return (
              <div key={campo} style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '1.2rem 1.4rem',
                background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 12,
              }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: ativo ? '#10b98122' : '#64748b22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icone size={18} style={{ color: ativo ? '#10b981' : '#64748b' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: '0 0 3px', fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-main)' }}>{titulo}</p>
                  <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>{desc}</p>
                </div>
                <button onClick={() => toggleConfig(campo)} style={{
                  padding: '7px 14px', borderRadius: 20,
                  border: `1px solid ${ativo ? '#10b981' : 'var(--border-color)'}`,
                  background: ativo ? '#10b98122' : 'transparent',
                  color: ativo ? '#10b981' : 'var(--text-muted)',
                  cursor: 'pointer', fontWeight: 700, fontSize: '0.78rem',
                  display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  {ativo ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
                  {ativo ? 'Ativo' : 'Inativo'}
                </button>
              </div>
            );
          })}

          {/* Status FCM */}
          <div style={{ padding: '1rem 1.4rem', background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 14 }}>
            {config.temFcm ? <Bell size={18} style={{ color: '#3b82f6' }} /> : <Bell size={18} style={{ color: '#64748b' }} />}
            <div>
              <p style={{ margin: 0, fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-main)' }}>Push Notifications</p>
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {config.temFcm
                  ? '✅ Seu celular está registrado para receber alertas.'
                  : '⚠️ Celular não registrado — alertas de segurança virão por e-mail.'}
              </p>
            </div>
          </div>

          {/* Passkey / Trust Level T5 */}
          <div style={{
            padding: '1.2rem 1.4rem', borderRadius: 12,
            border: `1px solid ${hasPasskey ? '#10b98144' : '#6c5ce744'}`,
            background: hasPasskey ? '#10b98108' : '#6c5ce708',
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: hasPasskey ? '#10b98122' : '#6c5ce722', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Fingerprint size={18} style={{ color: hasPasskey ? '#10b981' : '#6c5ce7' }} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ margin: '0 0 3px', fontWeight: 700, fontSize: '0.88rem', color: 'var(--text-main)' }}>Passkey (Estação Segura)</p>
              <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                {hasPasskey
                  ? '✅ Este computador já é uma Estação Segura (Trust Level T5).'
                  : 'Cadastre sua biometria para elevar este computador ao Trust Level T5.'}
              </p>
            </div>
            {!hasPasskey && (
              <button
                onClick={() => setShowPasskeyModal(true)}
                style={{
                  padding: '8px 16px', borderRadius: 9, border: 'none',
                  background: 'linear-gradient(135deg, #6c5ce7, #3b82f6)',
                  color: '#fff', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
                }}
              >
                <Fingerprint size={13} /> Cadastrar Passkey
              </button>
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
